
import { LocaleTypes, ThemingGenericContentSlotTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContent, MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { generateValueForContentProperty } from "@utils/content-util";
import { getVariablesFromMessagingTemplate } from "@utils/messaging-template-util";
import { convertJSONObjectToDotSeparatedString } from "@utils/string-util";
import cloneDeep from "lodash/cloneDeep";

export const getHtmlTextVariables = (htmlTextVariables: Record<string, string>, htmlText: string): { hasChanges: boolean; variables: Record<string, string> } => {
    const variables = getVariablesFromMessagingTemplate(htmlText);
    let hasChanges = false;

    let flattenedLanguageFile: { [key: string]: string } = {};
    if (htmlTextVariables) {
        flattenedLanguageFile = convertJSONObjectToDotSeparatedString(htmlTextVariables);
    }

    const finalVariables: Record<string, string> = {};

    variables.forEach(variable => {
        const { name, value } = variable;
        const isPropertyInLanguageFile = Object.keys(flattenedLanguageFile).includes(name);
        let updatedValue;

        if (!isPropertyInLanguageFile && value) {// the variable doesn't exist in language files and we have a default value
            updatedValue = value;
            hasChanges = true;
        } else if (isPropertyInLanguageFile) {// the variable already exist in language files empty or not 
            updatedValue = flattenedLanguageFile[name];
        } else {
            updatedValue = value || "";
        }

        finalVariables[name] = updatedValue;
    });

    if (variables.length !== Object.keys(htmlTextVariables).length) {
        hasChanges = true;
    }

    return { hasChanges, variables: finalVariables };
}

export const updateHelpTextCustomHtmlVariablesIntoAXNUIContent = (axnuiContent: AXNUIContent, htmlTextVariables: Record<string, string>, helpTextType: ThemingGenericContentSlotTypes): AXNUIContent => {
    return {
        ...axnuiContent,
        helpTextCustomHtmlVariables: {
            ...(axnuiContent?.helpTextCustomHtmlVariables || {}),
            [helpTextType]: htmlTextVariables
        }
    }
}

export const updateAllHelpTextCustomVariables = (
    htmlText: string,
    helpTextType: ThemingGenericContentSlotTypes,
    isMultiLanguageEnabled: boolean,
    availableLanguages: Array<LocaleTypes>,
    multiLanguageContent: MultiLanguageWrapper<AXNUIContent>,
    selectedLanguage: LocaleTypes,
    newContent: AXNUIContent,
    defaultContent: MultiLanguageWrapper<AXNUIContent>
): string => {
    if (isMultiLanguageEnabled) {
        const updatedMultiLanguageAXNUIContent = cloneDeep(multiLanguageContent);
        return availableLanguages.reduce((value, lng) => {
            if (lng !== selectedLanguage) {
                const { hasChanges, variables } = getHtmlTextVariables((updatedMultiLanguageAXNUIContent[lng]?.helpTextCustomHtmlVariables?.[helpTextType] || {}), htmlText);

                if (hasChanges) {
                    value = generateValueForContentProperty<AXNUIContent>(
                        isMultiLanguageEnabled,
                        updatedMultiLanguageAXNUIContent,
                        availableLanguages,
                        lng,
                        updateHelpTextCustomHtmlVariablesIntoAXNUIContent(multiLanguageContent[lng], variables, helpTextType),
                        defaultContent
                    );

                    updatedMultiLanguageAXNUIContent[lng] = JSON.parse(value)[lng];
                }
            } else {
                value = generateValueForContentProperty<AXNUIContent>(
                    isMultiLanguageEnabled,
                    updatedMultiLanguageAXNUIContent,
                    availableLanguages,
                    lng,
                    newContent,
                    defaultContent
                );

                updatedMultiLanguageAXNUIContent[lng] = newContent;
            }

            return value;
        }, "");
    } else {
        return generateValueForContentProperty<AXNUIContent>(
            false,
            null,
            availableLanguages,
            selectedLanguage,
            newContent,
            defaultContent
        );
    }
}